// visiblePoll.ts — INF-1677 (2026-08-27)
//
// WHY THIS EXISTS
// Neon bills COMPUTE-HOURS while the endpoint is awake and suspends after 5 minutes
// idle. A single forgotten browser tab polling every 20 seconds therefore costs the
// same as a busy production database — it simply never lets the compute sleep.
//
// Measured 2026-08-26/27: with all backend writes buffered and every cron convoyed,
// the endpoint was STILL awake 93.8% of the time. The remaining traffic was 170
// scans / 0 writes per 150s, hitting exactly the consumer map's table set
// (venue_locations, map_display_config, events, venues, canonical_matches).
// It was the PWA polling from open tabs. ~$30/month for nobody looking at anything.
//
// WHAT THIS FIXES
// A plain setInterval keeps firing when the tab is hidden, minimised, or on another
// desktop. This wrapper pauses while `document.visibilityState !== 'visible'` and
// fires once immediately on becoming visible again, so the user never sees stale
// data — they just stop paying for refreshes nobody is reading.
//
// USE THIS INSTEAD OF setInterval FOR ANYTHING THAT MAKES A NETWORK CALL.
// Pure UI timers (a clock ticking, a progress counter) do not need it — they cost
// nothing. Only network pollers do.

type Cleanup = () => void;

const canSeeDocument = () =>
  typeof document === 'undefined' || document.visibilityState === 'visible';

/**
 * Poll `fn` every `intervalMs`, but only while the tab is actually visible.
 *
 * - fires immediately on start (if visible)
 * - stops entirely while hidden — no timer, no requests
 * - fires once on regaining visibility, then resumes the interval
 *
 * Returns a cleanup function; call it from a useEffect teardown.
 */
export function visiblePoll(fn: () => void | Promise<void>, intervalMs: number): Cleanup {
  let timer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;

  const run = () => { try { void fn(); } catch { /* callers handle their own errors */ } };

  const start = () => {
    if (timer || stopped) return;
    timer = setInterval(() => { if (canSeeDocument()) run(); }, intervalMs);
  };

  const stop = () => { if (timer) { clearInterval(timer); timer = null; } };

  const onVisibility = () => {
    if (stopped) return;
    if (canSeeDocument()) {
      // Catch up on whatever was missed while hidden, then resume.
      run();
      start();
    } else {
      stop();
    }
  };

  if (canSeeDocument()) { run(); start(); }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  return () => {
    stopped = true;
    stop();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  };
}

/**
 * Same contract, but the first call is NOT made immediately — use when the caller
 * has already loaded once and only wants the refresh cadence.
 */
export function visiblePollDeferred(fn: () => void | Promise<void>, intervalMs: number): Cleanup {
  let first = true;
  return visiblePoll(() => { if (first) { first = false; return; } return fn(); }, intervalMs);
}
